import React, { useState } from 'react'
import { lmsAdmin } from '../api'
import { dateText, dateTimeText, utcText } from '../../../shared/date/local-time'
import { LmsIcon } from '../lms-icon'

// The connection's package signing key (plan Task 14C). Every connection
// signs its packages with its own key. The section says when the key was
// issued and, while a rotated key still validates old packages, when that
// stops. Rotate and Revoke are each behind a confirmation that says what
// happens to the packages already in the LMS, lists the courses that have
// one, and once done offers to rebuild them (the existing build route; a
// package built before the change carries the old key).

// RotationGraceDays mirrors the server's: a rotated key validates for 30 days.
const ROTATION_GRACE_DAYS = 30

const inGraceUntil = () => new Date(Date.now() + ROTATION_GRACE_DAYS * 24 * 60 * 60 * 1000)

const courseName = course => course.node_name || `Course #${course.node_id}`

// `readOnly`: the connection can no longer change (archived, or a role that may only read): the key is
// shown and cannot be rotated or revoked.
export const SigningKeySection = ({ connectionId, signingKey, onChange, readOnly }) => {
  const [action, updateAction] = useState(null) // 'rotate' | 'revoke' while the modal is open
  const [done, updateDone] = useState(false)
  const [courses, updateCourses] = useState(null)
  const [coursesError, updateCoursesError] = useState(null)
  const [busy, updateBusy] = useState(false)
  const [error, updateError] = useState(null)
  const [rebuilt, updateRebuilt] = useState({}) // node_id -> 'busy' | 'done' | an error message

  const open = async which => {
    updateAction(which)
    updateDone(false)
    updateError(null)
    updateRebuilt({})
    updateCourses(null)
    updateCoursesError(null)
    try {
      const result = await lmsAdmin.listConnectionCourses(connectionId)
      updateCourses(((result && result.courses) || []).filter(course => course.current_package_id))
    } catch (e) {
      updateCoursesError(e)
      updateCourses([])
    }
  }

  const close = () => {
    if (busy) return
    updateAction(null)
  }

  const confirm = async () => {
    updateBusy(true)
    updateError(null)
    try {
      const key = action === 'rotate'
        ? await lmsAdmin.rotateSigningKey(connectionId)
        : await lmsAdmin.revokeSigningKey(connectionId)
      onChange(key)
      updateDone(true)
    } catch (e) {
      updateError(e)
    } finally {
      updateBusy(false)
    }
  }

  const rebuild = async course => {
    updateRebuilt(prev => ({ ...prev, [course.node_id]: 'busy' }))
    try {
      await lmsAdmin.buildPackage(connectionId, Number(course.node_id))
      updateRebuilt(prev => ({ ...prev, [course.node_id]: 'done' }))
    } catch (e) {
      updateRebuilt(prev => ({ ...prev, [course.node_id]: e.message || 'The rebuild failed' }))
    }
  }

  const rebuildAll = async () => {
    for (const course of courses || []) {
      if (rebuilt[course.node_id] !== 'done') {
        await rebuild(course)
      }
    }
  }

  const stopDate = done && signingKey && signingKey.previous_valid_until
    ? dateText(signingKey.previous_valid_until)
    : dateText(inGraceUntil())

  const warning = action === 'rotate'
    ? `Packages built before today stop working on ${stopDate}. Rebuild them and replace them in your LMS.`
    : 'Every package for this connection stops working immediately until you rebuild and re-upload it.'

  return (
    <div className="form-section" data-testid="package-key-section">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="key" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Package key</h2>
      </div>
      <div className="field">
        {signingKey ? (
          <React.Fragment>
            <div className="field-help" data-testid="package-key-issued">
              Issued <span title={utcText(signingKey.issued_at)}>{dateTimeText(signingKey.issued_at)}</span>
            </div>
            {signingKey.previous_valid_until && (
              <div className="field-help" data-testid="package-key-grace">
                Packages built with the previous key stop working on{' '}
                <span title={utcText(signingKey.previous_valid_until)}>{dateTimeText(signingKey.previous_valid_until)}</span>.
              </div>
            )}
          </React.Fragment>
        ) : (
          <div className="field-help" data-testid="package-key-issued">This connection's key could not be read.</div>
        )}
        <div className="field-help">
          Every package for this connection is signed with this key.
          {readOnly ? " It can't be rotated or revoked while this connection can't be changed." : ' Rotate it on a schedule; revoke it if it may have leaked.'}
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-outline-secondary btn-sm" type="button" data-testid="package-key-rotate" onClick={() => open('rotate')}>
              Rotate key
            </button>
            <button className="btn btn-danger btn-sm" type="button" data-testid="package-key-revoke" onClick={() => open('revoke')}>
              Revoke key
            </button>
          </div>
        )}
      </div>

      {action && (
        <div className="lms-modal-scrim" data-testid={`package-key-${action}-modal`}>
          <div
            className="lms-modal"
            role="dialog"
            aria-modal="true"
            aria-label={action === 'rotate' ? 'Rotate the package key?' : 'Revoke the package key?'}
          >
            <div className="modal-head">
              <div className="icon">
                <LmsIcon name={action === 'rotate' ? 'rotate' : 'triangle-exclamation'} />
              </div>
              <div className="body">
                <h2>
                  {done
                    ? action === 'rotate' ? 'Key rotated' : 'Key revoked'
                    : action === 'rotate' ? 'Rotate the package key?' : 'Revoke the package key?'}
                </h2>
              </div>
              <button className="close-btn" title="Close" type="button" onClick={close}>
                <LmsIcon name="xmark" />
              </button>
            </div>
            <div className="lms-modal-body">
              <p className="field-help" style={{ marginTop: 0 }} data-testid="package-key-warning">
                {done && <React.Fragment>A new key signs every package you build from now on. </React.Fragment>}
                {warning}
              </p>
              {error && (
                <div className="error-banner" data-testid="package-key-error">
                  <LmsIcon name="circle-exclamation" />
                  <div>
                    <p className="title">{error.message}</p>
                  </div>
                </div>
              )}
              <div className="field-label">
                <span className="name">{done ? 'Rebuild these courses' : 'Courses with a package'}</span>
              </div>
              {courses === null && <div className="field-help">Loading courses…</div>}
              {coursesError && <div className="field-help">The courses could not be listed: {coursesError.message}</div>}
              {courses && courses.length === 0 && !coursesError && (
                <div className="field-help" data-testid="package-key-no-courses">No course has a package yet.</div>
              )}
              {courses && courses.length > 0 && (
                <ul className="list-unstyled mb-0" data-testid="package-key-courses">
                  {courses.map(course => {
                    const state = rebuilt[course.node_id]
                    return (
                      <li key={course.node_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                        <span style={{ flex: 1 }}>{courseName(course)}</span>
                        {done && state === 'done' && <span className="pill success">Rebuilt</span>}
                        {done && state && state !== 'done' && state !== 'busy' && <span className="pill danger">{state}</span>}
                        {done && state !== 'done' && (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            disabled={state === 'busy'}
                            data-testid={`package-key-rebuild-${course.node_id}`}
                            onClick={() => rebuild(course)}
                          >
                            {state === 'busy' ? 'Rebuilding…' : 'Rebuild'}
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="modal-foot">
              <span className="help" />
              {done ? (
                <React.Fragment>
                  {courses && courses.length > 1 && (
                    <button className="btn btn-outline-secondary btn-sm" type="button" data-testid="package-key-rebuild-all" onClick={rebuildAll}>
                      Rebuild all
                    </button>
                  )}
                  <button className="btn btn-light border btn-sm" type="button" data-testid="package-key-close" onClick={close}>
                    Close
                  </button>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <button className="btn btn-light border btn-sm" type="button" data-testid="package-key-cancel" disabled={busy} onClick={close}>
                    Cancel
                  </button>
                  <button
                    className={`btn ${action === 'rotate' ? 'btn-outline-secondary' : 'btn-danger'} btn-sm`}
                    type="button"
                    data-testid="package-key-confirm"
                    disabled={busy}
                    onClick={confirm}
                  >
                    {action === 'rotate' ? 'Rotate key' : 'Revoke key'}
                  </button>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
